import { useState } from "react";
import FormInput from "./FormInput";
import FormTextArea from "./FormTextArea";

type FormStatus = "idle" | "loading" | "success" | "error";

export function ContactForm() {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        company: "",
        message: "",
    });
    const [status, setStatus] = useState<FormStatus>("idle");
    const [errorMessage, setErrorMessage] = useState("");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus("loading");
        setErrorMessage("");

        try {
            const response = await fetch("https://formspree.io/f/xjggakre", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                setStatus("success");
                setFormData({ name: "", email: "", company: "", message: "" });
            } else {
                const data = await response.json();
                if (data.errors) {
                    setErrorMessage(data.errors.map((error: any) => error.message).join(", "));
                } else {
                    setErrorMessage("Something went wrong. Please try again later.");
                }
                setStatus("error");
            }
        } catch (error) {
            setErrorMessage("Network error. Please check your connection.");
            setStatus("error");
        }
    };

    return (
        <section className="py-16 w-full bg-wine" id="contact">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center justify-end">
                    <h2 className="font-display text-6xl md:text-8xl font-black uppercase mb-4">
                        Let's work together!
                    </h2>
                    <p className="w-full md:w-1/2 text-gray-600 mb-12 text-xs ">
                        I'm here to help you bring your ideas to life. Whether you need a website, an app, or a
                        custom solution, I'm here to help.
                    </p>
                </div>

                <div className="">
                    <form onSubmit={handleSubmit} className="space-y-6 font-light">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            <FormInput
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                autoComplete="name"
                                placeholder="Name"
                            />

                            <FormInput
                                type="text"
                                id="company"
                                name="company"
                                value={formData.company}
                                onChange={handleChange}
                                required
                                autoComplete="organization"
                                placeholder="Company name"
                            />

                            <FormInput
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                autoComplete="email"
                                placeholder="email@example.com"
                            />
                        </div>

                        <div className="flex flex-col md:flex-row gap-8 items-end">
                            <div className="w-full md:w-2/3">
                                <FormTextArea
                                    id="message"
                                    name="message"
                                    label="Type message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                    rows={1}
                                    autoComplete="off"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={status === "loading"}
                                className={`underline p-0 text-xs mb-1 font-bold cursor-pointer transition-opacity ${status === "loading" ? "opacity-50 cursor-not-allowed" : "hover:opacity-70"
                                    }`}
                            >
                                {status === "loading" ? "Sending..." : "I want to talk to you →"}
                            </button>
                        </div>

                        {/* Status Messages */}
                        <div className="min-h-[20px] text-xs">
                            {status === "success" && (
                                <p className="font-medium animate-in fade-in slide-in-from-top-1">
                                    Message sent successfully! I'll get back to you soon.
                                </p>
                            )}
                            {status === "error" && (
                                <p className="font-medium animate-in fade-in slide-in-from-top-1">
                                    {errorMessage}
                                </p>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}

export default ContactForm;
